<ng-template #SettingsPopupTemplate>
  <mir-popup #SettingsPopup class="settings-popup">

    <popup-header class="settings-popup-header">
        <MirIcon>settings</MirIcon>
        <h2 class="settings-modal-title text-carbon font-medium">{{'Settings.Title' | translate}}</h2>
    </popup-header>

    <popup-content class="settings-popup-content">

      <section class="size-full flex flex-col gap-y-8 mb-2">
        <section class="setting-section">
          <h3 class="setting-section-title">{{'Settings.Language.Title' | translate}}</h3>
          <div class="setting-content">
            <mir-select
              [attr.aria-label]="'Settings.Language.Title' | translate"
              [options]="languages()"
              (selectionChange)="handleChangeLanguage($event)">
            </mir-select>
          </div>
        </section>

        <section class="size-full flex flex-col mb-2">

          <section class="setting-section">
            <h3 class="setting-section-title">
              <MirIcon >lock</MirIcon>
              {{'Settings.ChangePassword.Title' | translate}}</h3>

            <div class="setting-content">

              <div class="setting-form-field">
                <label [attr.for]="inputIds.old">{{'Settings.ChangePassword.Old' | translate}}</label>
                <div class="relative">
                  <input mirInput
                        autocomplete="new-password"
                        class="setting-password-field !pr-10"
                        [attr.id]="inputIds.old"
                        [attr.type]="visibilityStates().old ? 'text' : 'password'"
                        max="256"
                    [class.error]="oldPassword.touched && errorStates().old"
                        [placeholder]="'Settings.ChangePassword.Old' | translate"
                        [formControl]="oldPassword"
                        (blur)="handleBlurPassword($event, 'old')">
                  <button class="settings-password-button"
                      type="button"
                      [attr.aria-label]="visibilityStates().old ? (('Settings.ChangePassword.HidePassword' | translate)) : (('Settings.ChangePassword.ShowPassword' | translate))"
                      (click)="handleToggleVisibility($event, 'old')">
                    <MirIcon>{{getVisibilityIcon('old')}}</MirIcon>
                  </button>
                </div>
                <div class="setting-error-msg">
                  @if (oldPassword.touched && errorStates().old) {
                    {{'UI.MandatoryField' | translate}}
                  }
                </div>
              </div>

              <div class="setting-form-field">
                <label [attr.for]="inputIds.new">{{'Settings.ChangePassword.New' | translate}}</label>
                <div class="relative">
                  <input mirInput
                        autocomplete="new-password"
                        class="setting-password-field !pr-10"
                        [attr.id]="inputIds.new"
                        [min]="MIN_LENGTH"
                        max="256"
                        [attr.type]="visibilityStates().new ? 'text' : 'password'"
                    [class.error]="newPassword.touched && (errorStates().new || errorStates().same)"
                        [placeholder]="'Settings.ChangePassword.New' | translate"
                        [formControl]="newPassword"
                        (blur)="handleBlurPassword($event, 'new')">
                  <button class="settings-password-button"
                      type="button"
                      [attr.aria-label]="visibilityStates().new ? (('Settings.ChangePassword.HidePassword' | translate)) : (('Settings.ChangePassword.ShowPassword' | translate))"
                      (click)="handleToggleVisibility($event, 'new')">
                    <MirIcon>{{getVisibilityIcon('new')}}</MirIcon>
                  </button>
                </div>
                <div class="setting-error-msg">
                  @if (newPassword.touched && errorStates().new) {
                    {{'UI.MandatoryField' | translate}}
                  } @else if (newPassword.touched && errorStates().same) {
                    {{'Settings.ChangePassword.SamePassword' | translate}}
                  }
                </div>

                <div class="password-strength-bar"
                    role="presentation"
                    aria-hidden="true">
                  @for (segment of [0, 1, 2, 3]; track segment) {
                    <span class="password-strength-segment"
                        [ngClass]="getSegmentClass(segment)">
                    </span>
                  }
                </div>

                <div class="password-criteria-grid">
                  <div class="password-criteria-item"
                      [class.password-criteria-item-valid]="isCriteriaValid('minLength')"
                      [class.password-criteria-item-invalid]="!isCriteriaValid('minLength')">
                    @if (isCriteriaValid('minLength')) {
                      <MirIcon class="password-criteria-check">check</MirIcon>
                    } @else {
                      <span class="password-criteria-bullet">•</span>
                    }
                    <span>{{'Settings.ChangePassword.Criteria.MinLength' | translate}}</span>
                  </div>

                  <div class="password-criteria-item"
                      [class.password-criteria-item-valid]="isCriteriaValid('uppercase')"
                      [class.password-criteria-item-invalid]="!isCriteriaValid('uppercase')">
                    @if (isCriteriaValid('uppercase')) {
                      <MirIcon class="password-criteria-check">check</MirIcon>
                    } @else {
                      <span class="password-criteria-bullet">•</span>
                    }
                    <span>{{'Settings.ChangePassword.Criteria.Uppercase' | translate}}</span>
                  </div>

                  <div class="password-criteria-item"
                      [class.password-criteria-item-valid]="isCriteriaValid('digit')"
                      [class.password-criteria-item-invalid]="!isCriteriaValid('digit')">
                    @if (isCriteriaValid('digit')) {
                      <MirIcon class="password-criteria-check">check</MirIcon>
                    } @else {
                      <span class="password-criteria-bullet">•</span>
                    }
                    <span>{{'Settings.ChangePassword.Criteria.Digit' | translate}}</span>
                  </div>

                  <div class="password-criteria-item"
                      [class.password-criteria-item-valid]="isCriteriaValid('special')"
                      [class.password-criteria-item-invalid]="!isCriteriaValid('special')">
                    @if (isCriteriaValid('special')) {
                      <MirIcon class="password-criteria-check">check</MirIcon>
                    } @else {
                      <span class="password-criteria-bullet">•</span>
                    }
                    <span>{{'Settings.ChangePassword.Criteria.Special' | translate}}</span>
                  </div>
                </div>
              </div>

              <div class="setting-form-field">
                <label [attr.for]="inputIds.confirm">{{'Settings.ChangePassword.Confirm' | translate}}</label>
                <div class="relative">
                  <input mirInput
                        autocomplete="new-password"
                        class="setting-password-field !pr-10"
                        [attr.id]="inputIds.confirm"
                        [min]="MIN_LENGTH"
                        max="256"
                        [attr.type]="visibilityStates().confirm ? 'text' : 'password'"
                        [class.error]="confirmPassword.touched && (errorStates().confirm || errorStates().noMatch)"
                        [placeholder]="'Settings.ChangePassword.Confirm' | translate"
                        [formControl]="confirmPassword"
                        (blur)="handleBlurPassword($event, 'confirm')">
                  <button class="settings-password-button"
                      type="button"
                      [attr.aria-label]="visibilityStates().confirm ? (('Settings.ChangePassword.HidePassword' | translate)) : (('Settings.ChangePassword.ShowPassword' | translate))"
                      (click)="handleToggleVisibility($event, 'confirm')">
                    <MirIcon>{{getVisibilityIcon('confirm')}}</MirIcon>
                  </button>
                </div>
                <div class="setting-error-msg">
                  @if (confirmPassword.touched && errorStates().confirm) {
                    {{'UI.MandatoryField' | translate}}
                  } @else if (confirmPassword.touched && errorStates().noMatch) {
                    {{'Settings.ChangePassword.NoMatch' | translate}}
                  }
                </div>
              </div>

            </div>
          </section>

          @if (inlineAlertMessage(); as alertMessage) {
            <div class="settings-inline-alert"
                role="alert"
                aria-live="polite">
              <MirIcon class="settings-inline-alert-icon">Warning</MirIcon>
              <span>{{alertMessage}}</span>
            </div>
          }
        </section>
      </section>

    </popup-content>

    <popup-footer class="settings-popup-footer">
      <button mirButton stroked
          (click)="handleClose()">
        <MirIcon>cancel</MirIcon>
        {{'UI.Close' | translate}}
      </button>

      <button type="submit" mirButton stroked primary
          [disabled]="!canSubmitPassword()"
          (click)="handleSendNewPassword()">
        @if (isSendingPassword()) {
          <SpinnerIcon/>
          {{'UI.Updating' | translate}}
        } @else {
          <MirIcon>check</MirIcon>
          {{'UI.Update' | translate}}
        }
      </button>
    </popup-footer>
  </mir-popup>
</ng-template>
